import React, { useEffect, useState } from "react";
import _ from "lodash";
import moment from "moment";
import { bindActionCreators, compose } from "redux";
import { withRouter } from "react-router";
import { connect } from "react-redux";
import { CardPageLayout } from "component-lib/CardPageLayout";
import { CustomizableHeroCard } from "component-lib/CustomizableHeroCard";
import {
  LabeledButtonWithCount,
  MaterialIcon,
  LabelString,
  LabelDollarFormatted,
  ModuleAccordionGroup,
  ModuleAccordionSection,
  ButtonText
} from "@loanbossinc/component-library";
import FolderIcon from "@material-ui/icons/Folder";
import UserConfirmationModal from "modals/UserConfirmationModal/UserConfirmationModal";

import LenderReportingDrawer from "../../../drawer";
import calcIcon from "../../../calcIcon.svg";
import ConfigPageDisclaimer from "../../configurationPageDisclaimer";
import { dealGet } from "../../../../deal/deal.store";
import { actionCreators as dashboardActionCreators } from "../../../dashboard/dashboard.store";
import { actionCreators as debtYieldSetupActionCreators } from "../../../reportComponentsSetup/debtYieldSetup/debtYieldSetup.store";
import { actionCreators as debtYieldDrawerActionCreators } from "../../../drawer/debt-yield-line-items-management/debtYieldLineItemManagement.store";
import { actionCreators as reportComponentActionCreators } from "../../reportComponent.store";
import { actionCreators as reportConfigActionCreators } from "../../../reportingSetup/reportingSetup.store";
import {
  StickyWrapper,
  LabeledIconWrapper,
  Container
} from "../../../dashboard/styles";
import { ListItemContent, ListItemText, ListItemTitle } from "../../styles";

import { actionCreators as debtYieldCalculationActionCreators } from "./debtYieldProjectedCalculationDashboard.store";
import {
  SourceFileTypeWrapper,
  StyledChevronRight,
  StyledIconButton,
  StyledSelectCalcRow,
  LBRowDebtString,
  LBRowDerivedString,
  LineItemDescription,
  LineItemRowContainer,
  PropertyNameWrapper,
  UploadDateWrapper
} from "../styles";

const formattedDollarLabel = (value, fontWeight = "bold") => (
  <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
    <LabelDollarFormatted
      value={(value && Math.abs(value)) || " "}
      hasAccountingFormat={value < 0}
      decimals={2}
      style={{ fontWeight, color: value < 0 ? "#C40A0A" : "#000" }}
    />
  </div>
);

const LineItemRow = props => {
  const {
    lineItem,
    loanId,
    deal,
    setDebtYieldCalculationDashboardSelections,
    setDYLineItemSelections
  } = props;
  const {
    value,
    lineItemType,
    fileTypeName,
    fileCreatedAt,
    propertyName,
    sourceName
  } = lineItem;

  const handleEditClick = () => {
    setDebtYieldCalculationDashboardSelections({
      isLenderReportingDrawerOpen: true,
      initialDrawerSectionName: "debt-yield-line-items"
    });
    setDYLineItemSelections({
      lineItemType,
      activeLoanId: loanId,
      selectedLineItem: lineItem,
      selectedLoanBalance: lineItemType === "derivedFromLB" ? value : null
    });
  };

  const amountSource = lineItem.outstandingDebtSelected !== false ? "Outstanding Debt" : "Total Loan Amount";

  return (
    <LineItemRowContainer>
      <StyledIconButton disableRipple onClick={handleEditClick}>
        <MaterialIcon
          style={{ color: "#005AA9", width: "18px" }}
          iconName="create"
        />
      </StyledIconButton>
      {lineItemType === "fromFile" ? (
        <LineItemDescription>
          <SourceFileTypeWrapper>
            {_.startCase(`${sourceName} ${fileTypeName}`)}
          </SourceFileTypeWrapper>
          <UploadDateWrapper>
            {`Uploaded on ${moment
              .utc(fileCreatedAt)
              .format("MMMM DD, YYYY")}`}
          </UploadDateWrapper>
          <PropertyNameWrapper>{propertyName}</PropertyNameWrapper>
        </LineItemDescription>
      ) : (
        <LineItemDescription>
          <LBRowDebtString>{amountSource}</LBRowDebtString>
          <LBRowDerivedString>Derived From LoanBoss</LBRowDerivedString>
        </LineItemDescription>
      )}
      {formattedDollarLabel(value, "400")}
    </LineItemRowContainer>
  );
};

const LoanAccordionGroup = props => {
  const {
    loanDYCalculation,
    debtYieldCalculations,
    setDebtYieldSetupSelections,
    setDebtYieldCalculationDashboardSelections,
    setDYLineItemSelections,
    deal
  } = props;
  const { id, lineItem, loanDebtYieldTotal, name } = loanDYCalculation;
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (lineItem) {
      const updatedTotalValuesForLoans =
        debtYieldCalculations &&
        debtYieldCalculations.map(calc => {
          if (calc.id !== id) {
            return calc;
          }

          return { ...calc, loanDebtYieldTotal: calc.lineItem?.value };
        });
      setDebtYieldSetupSelections({
        debtYieldCalculations: updatedTotalValuesForLoans
      });
    }
  }, [lineItem]);

  useEffect(() => {
    setIsExpanded(true);
  }, []);

  const handleExpansion = e => {
    if (
      e.target.innerHTML.includes("unfold_") &&
      e.target.tagName.toLowerCase() === "i"
    ) {
      setIsExpanded(prev => !prev);
    }
  };

  const getLineItem = () => (
    <LineItemRow
      lineItem={lineItem}
      loanId={id}
      deal={deal}
      setDebtYieldCalculationDashboardSelections={
        setDebtYieldCalculationDashboardSelections
      }
      setDYLineItemSelections={setDYLineItemSelections}
    />
  );

  const handleSelectClick = () => {
    setDebtYieldCalculationDashboardSelections({
      isLenderReportingDrawerOpen: true,
      initialDrawerSectionName: "debt-yield-line-items"
    });

    setDYLineItemSelections({
      activeLoanId: id
    });
  };

  const getSelectCalcMethodRow = () => (
    <StyledSelectCalcRow>
      <ButtonText
        label="Select calculation method"
        onClick={handleSelectClick}
      />
      <StyledChevronRight onClick={handleSelectClick} />
    </StyledSelectCalcRow>
  );

  return (
    loanDYCalculation && (
      <div style={{ marginBottom: "2em" }}>
        <ModuleAccordionGroup
          onClick={handleExpansion}
          shouldExpandAll={isExpanded}
          shouldCollapseAll={!isExpanded}
          groups={[
            {
              title: name,
              key: id,
              isTitleGroup: false,
              isExpandable: true,
              expandButtonPosition: "left",
              titleAdornmentRight:
                !isExpanded && formattedDollarLabel(loanDebtYieldTotal, "400"),
              sections: [lineItem ? getLineItem() : getSelectCalcMethodRow()]
            }
          ]}
        />
      </div>
    )
  );
};

const DebtYieldProjectedCalculationDashboard = props => {
  const {
    deal,
    history,
    location,
    initialDrawerSectionName,
    isLenderReportingDrawerOpen,
    debtYieldCalculations,
    debtYieldReportComponent,
    sourceFileLineItem,
    activeLoanId,
    sourceFileCount,
    fieldCount,
    totalDebtYieldValue,
    selectedLoansForDY,
    setDebtYieldCalculationDashboardSelections,
    selectedReportGroup,
    getFieldCountForDeal,
    getReportGroupById,
    getSourceFileCountForDeal,
    setDebtYieldSetupSelections,
    putProjectedReportComponentConfig,
    postProjectedReportComponentConfig,
    putReportConfig,
    match: {
      params: { dealId, reportId, reportComponentId }
    },
    user,
    setReportComponentSelections,
    isSaving,
    wasNewReportComponentSaved,
    savedReportComponent
  } = props;

  const [wasContentUpdated, setWasContentUpdated] = useState(false);
  const [showDialog, setShowDialog] = useState(false);


  useEffect(() => {
    if (!selectedReportGroup && reportId) {
      getReportGroupById({ reportGroupId: reportId });
    }
  }, [selectedReportGroup, reportId]);

  useEffect(() => {
    if (_.isEmpty(deal)) {
      props.dealGet({ dealId });
    }

    return () => {
      setDebtYieldSetupSelections({
        selectedLoansForDY: [],
        debtYieldCalculations: null
      });
      setReportComponentSelections({ isSaving: false, isSavingComplete: false, wasNewReportComponentSaved: false, savedReportComponent: null });
    };
  }, [dealId, deal, reportId]);

  useEffect(() => {
    if (Boolean(dealId) || !isLenderReportingDrawerOpen) {
      getFieldCountForDeal({ dealId });
      getSourceFileCountForDeal({ dealId });
    }
  }, [dealId, isLenderReportingDrawerOpen]);

  useEffect(() => {
    if (!selectedLoansForDY.length) {
      history.push(
        `/deal/${dealId}/lender-reports-debt-yield-config/${reportId}`
      );
    } else {
      const loansForCalculations =
        selectedLoansForDY &&
        selectedLoansForDY.map(loan => {
          const existingCalculation =
            debtYieldCalculations &&
            debtYieldCalculations.find(calc => calc.id === loan.id);
          if (existingCalculation) {
            return existingCalculation;
          }

          return { ...loan, lineItem: null, loanDebtYieldTotal: null };
        });
      setDebtYieldSetupSelections({
        debtYieldCalculations: loansForCalculations
      });
    }
  }, [reportComponentId, selectedLoansForDY]);

  // updates debt yield calculations if the range was selected in selected source file
  useEffect(() => {
    if (sourceFileLineItem) {
      const updatedLoanCalculations =
        debtYieldCalculations &&
        debtYieldCalculations.map(calc => {
          if (calc.id !== activeLoanId) {
            return calc;
          }
          const { selectedSourceFile, value } = sourceFileLineItem;
          const { typeName, createdAt, sourceName } = selectedSourceFile;

          let propertyName = "Deal";
          if (
            selectedSourceFile.propertyId &&
            selectedSourceFile.propertyId !== "-1"
          ) {
            const dealProperty =
              Boolean(deal) &&
              deal.properties.find(x => x.id === selectedSourceFile.propertyId);
            if (dealProperty) {
              propertyName = dealProperty.name;
            } else {
              propertyName = "Property";
            }
          }

          const updatedLineItem = {
            lineItemType: "fromFile",
            fileTypeName: typeName,
            fileCreatedAt: createdAt,
            propertyName,
            sourceName,
            value
          };

          return {
            ...calc,
            lineItem: updatedLineItem,
            loanDebtYieldTotal: value
          };
        });
      setDebtYieldSetupSelections({
        debtYieldCalculations: updatedLoanCalculations
      });
    }
  }, [sourceFileLineItem]);

  // updates total debt yield value
  useEffect(() => {
    if (debtYieldCalculations) {
      const totalDebtYieldValueForReportComponent = debtYieldCalculations.reduce(
        (sum, val) => (val.loanDebtYieldTotal !== null ? sum + val.loanDebtYieldTotal : sum),
        0
      );

      if (totalDebtYieldValueForReportComponent !== totalDebtYieldValue) {
        setWasContentUpdated(true);
      }
      setDebtYieldSetupSelections({
        totalDebtYieldValue: totalDebtYieldValueForReportComponent
      });
    }
  }, [debtYieldCalculations]);

  useEffect(() => {
    if (wasNewReportComponentSaved) {
      setDebtYieldSetupSelections({ debtYieldReportComponent: savedReportComponent });
    }
  }, [wasNewReportComponentSaved, savedReportComponent]);

  const closeDrawer = () => {
    setDebtYieldCalculationDashboardSelections({
      isLenderReportingDrawerOpen: false,
      initialDrawerSectionName: null
    });
  };

  const redirectOnSaveOrCancel = () => {
    if (location.state.from === "report-wizard") {
      history.push(`/deal/${dealId}/lender-reports-setup/${reportId}`, {
        from: "undefined-2"
      });
    } else {
      history.push(`/deal/${dealId}/lender-reports/${reportId}`);
    }
  };

  const onSave = () => {
    if (reportComponentId === "new" && !wasNewReportComponentSaved) {
      const payload = {
        type: "DebtYield",
        reportGroupId: reportId,
        createdBy: user.id,
        dealId,
        calculations: {
          selectedLoans: debtYieldCalculations,
          totalDebtYieldValue
        },
        isProjected: true
      };
      postProjectedReportComponentConfig({ data: payload });
    } else {
      const payload = {
        ...debtYieldReportComponent,
        calculations: {
          selectedLoans: debtYieldCalculations,
          totalDebtYieldValue
        },
        isProjected: true
      };
      putProjectedReportComponentConfig({
        data: _.omit(payload, ["updatedAt", "createdAt"])
      });
    }

    putReportConfig({
      data: { id: reportId, debtYieldProjectedLastUpdatedDate: new Date().toISOString() }
    });

    setWasContentUpdated(false);
  };

  const onCancelSaving = () => {
    redirectOnSaveOrCancel();
  };

  const onBackButtonClick = () => {
    if (wasContentUpdated) {
      setShowDialog(true);
    } else {
      redirectOnSaveOrCancel();
    }
  };

  const onConfirmDialog = () => {
    onSave();
    setShowDialog(false);
    redirectOnSaveOrCancel();
  };

  const saveFormulaButton = {
    label: isSaving ? "Saving..." : "Save",
    onClick: onSave,
    disabled: isSaving
  };
  const cancelFormulaButton = {
    label: "Cancel changes",
    onClick: onCancelSaving
  };

  const heroCardContents = () => {
    if (!selectedReportGroup) {
      return null;
    }
    const { reportName, frequency, firstReportDueDate } = selectedReportGroup;

    return (
      <ListItemContent>
        <ListItemTitle>{reportName}</ListItemTitle>
        <ListItemText>
          {`Published ${frequency} and started on ${moment
            .utc(firstReportDueDate)
            .format("MMMM Do, YYYY")}`}
        </ListItemText>
      </ListItemContent>
    );
  };

  const heroCard = () => {
    if (!deal) {
      return null;
    }

    return (
      <StickyWrapper>
        <CustomizableHeroCard
          {...props}
          buttons={[saveFormulaButton, cancelFormulaButton]}
          cardContent={heroCardContents()}
          title={deal.name}
          backLabel="All calculations"
          onBackButtonClick={onBackButtonClick}
        />
        <LabeledIconWrapper>
          <Container>
            <LabeledButtonWithCount
              label="Source Files"
              icon={<FolderIcon />}
              count={sourceFileCount}
              style={{
                width: "100%",
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                backgroundColor: "#fff"
              }}
              onClick={() => setDebtYieldCalculationDashboardSelections({
                isLenderReportingDrawerOpen: true,
                initialDrawerSectionName: "source-file-management"
              })}
            />
            <LabeledButtonWithCount
              label="Fields"
              icon={
                <img src={calcIcon} alt="Field Icon" height={18} width={18} />
              }
              count={fieldCount}
              style={{
                width: "100%",
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                backgroundColor: "#fff"
              }}
              onClick={() => setDebtYieldCalculationDashboardSelections({
                isLenderReportingDrawerOpen: true,
                initialDrawerSectionName: "field-management"
              })}
            />
          </Container>
        </LabeledIconWrapper>
      </StickyWrapper>
    );
  };

  const body = () => debtYieldCalculations && (
    <div style={{ flexDirection: "column" }}>
      <div style={{ marginBottom: 20 }}>
        <ConfigPageDisclaimer
          style={{ marginBottom: 20, backgroundColor: "white" }}
        />
      </div>
      {debtYieldCalculations?.map(dyLoanCalc => (
        <LoanAccordionGroup
          loanDYCalculation={dyLoanCalc}
          {...props}
          key={dyLoanCalc.id}
        />
      ))}
      <ModuleAccordionSection
        isExpandable={false}
        title={
          <LabelString value="Projected Total" lines={1} style={{ fontSize: "18px" }} />
        }
        titleAdornmentRight={formattedDollarLabel(totalDebtYieldValue, "400")}
        style={{ borderRadius: "8px", backgroundColor: "#FFFFFF" }}
      />
    </div>
  );

  return (
    <>
      <CardPageLayout
        isReversed
        bannerImageUrl={deal && deal.imageLarge}
        cardComponent={heroCard()}
        bodyComponent={body()}
      />
      <LenderReportingDrawer
        authentication={props.authentication}
        closeDrawer={closeDrawer}
        deal={deal}
        initialDrawerSectionName={initialDrawerSectionName}
        isOpen={isLenderReportingDrawerOpen}
      />
      <UserConfirmationModal
        open={showDialog}
        onClose={() => setShowDialog(false)}
        onCancelClick={onCancelSaving}
        onConfirmClick={onConfirmDialog}
      />
    </>
  );
};

const mapStateToProps = ({
  lenderReportList,
  lenderReportsDebtYieldSetup,
  lenderReportsDebtYieldCalculation,
  lenderReportDebtYieldLineItemManagement,
  lenderReportingReportComponent,
  newDeal: { deal },
  authentication: { user }
}) => ({
  ...lenderReportList,
  ...lenderReportsDebtYieldSetup,
  ...lenderReportsDebtYieldCalculation,
  ...lenderReportDebtYieldLineItemManagement,
  ...lenderReportingReportComponent,
  deal,
  user
});

const mapDispatchToProps = dispatch => bindActionCreators(
  {
    dealGet,
    ...dashboardActionCreators,
    ...debtYieldSetupActionCreators,
    ...debtYieldCalculationActionCreators,
    ...debtYieldDrawerActionCreators,
    ...reportComponentActionCreators,
    ...reportConfigActionCreators
  },
  dispatch
);

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(DebtYieldProjectedCalculationDashboard);
